import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import {
  Calculation,
  List as CalculationList,
} from "@/services/backend/qpm/calculations/types/calculation";
import {
  useQpmCalculationListQuery,
  useQpmCalculationShowQuery,
} from "@/services/backend/qpm/calculations/api/calculation-api";
import { useQpmOfferApplyCalculationMutation } from "@/services/backend/qpm/offers/api/offer-api";
import {
  ApplyCalculationDataRequest,
  Offer,
} from "@/services/backend/qpm/offers/types/offer";
import { Button } from "@/shared/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { CreateFromCalculation } from "@/routes/processes/[processId]/qpm/offers/_components/create-from-calculation";
import { ReactNode } from "react";

export type SelectCalculationComponentProps = {
  offer: Offer;
  customerId: string;
  processId: string;
};

export function SelectCalculationCardComponent({
  offer,
  customerId,
  processId,
}: SelectCalculationComponentProps) {
  return (
    <div className="mb-2">
      <Card>
        <CardHeader>
          <CardTitle>{t("Kalkulationen")}</CardTitle>
          <CardDescription>
            {t(
              "Wählen Sie hier die Kalkulation, auf Basis derer Sie ein Angebot erstellen möchten.",
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <SelectCalculationComponent
              offer={offer}
              customerId={customerId}
              processId={processId}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function SelectCalculationComponent({
  offer,
  customerId,
  processId,
}: SelectCalculationComponentProps) {
  const {
    data: list,
    isLoading,
    error,
  } = useQpmCalculationListQuery({
    process: {
      active: true,
      values: [processId],
    },
    customer: {
      active: true,
      values: [customerId],
    },
  });

  const calculationId = offer.linkedCalculationId;

  const skipQuery = !calculationId;

  const {
    data: selectedCalculation,
    isLoading: isLoadingSelectedCalculation,
    error: errorSelectedCalculation,
  } = useQpmCalculationShowQuery({ id: calculationId! }, { skip: skipQuery });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (errorSelectedCalculation) {
    return <RTKQueryErrorAlert error={errorSelectedCalculation} />;
  }

  if (isLoading || isLoadingSelectedCalculation) {
    return (
      <div className="mb-2">
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <CalculationSelector
      offerId={offer.id}
      calculationList={list!}
      selectedCalculation={selectedCalculation}
      immutable={offer.immutable}
    />
  );
}

type CalculationDropDownProps = {
  offerId: string;
  calculationList: CalculationList;
  selectedCalculation?: Calculation;
  immutable?: boolean;
};

export function CalculationSelector({
  offerId,
  calculationList,
  selectedCalculation,
  immutable,
}: CalculationDropDownProps) {
  const [applyCalculationData, { error, isLoading }] =
    useQpmOfferApplyCalculationMutation();

  const onChange = (value: string) => {
    if (immutable) {
      return;
    }
    const calculation = calculationList.items.find((req) => req.id === value);
    if (!calculation || isLoading || error) {
      return;
    }
    const request: ApplyCalculationDataRequest = {
      customerId: calculation.customerId,
      offerId,
      calculationId: calculation.id,
    };

    applyCalculationData(request);
  };

  return (
    <Select
      defaultValue={selectedCalculation?.id}
      value={selectedCalculation?.id}
      disabled={immutable || isLoading}
      onValueChange={(value: string) => {
        onChange(value);
      }}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={t("Kalkulationen")} />
      </SelectTrigger>
      <SelectContent>
        {calculationList.items.map((calculation) => (
          <SelectItem key={calculation.id} value={calculation.id}>
            <CalculationDisplayRow calculation={calculation} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export type SelectCalculationButtonComponentProps = {
  offerId?: string;
  processId: string;
  selectedCalculationId?: string;
};

export function SelectCalculationButtonComponent({
  offerId,
  processId,
  selectedCalculationId,
}: SelectCalculationButtonComponentProps) {
  const {
    data: selectedCalculation,
    isLoading: isLoadingSelectedCalculation,
    error: errorSelectedCalculation,
  } = useQpmCalculationShowQuery(
    { id: selectedCalculationId! },
    { skip: !selectedCalculationId },
  );

  if (errorSelectedCalculation) {
    return <RTKQueryErrorAlert error={errorSelectedCalculation} />;
  }

  if (isLoadingSelectedCalculation) {
    return <Loader2 className="animate-spin" />;
  }

  return (
    <SelectDocumentButton selectedDocumentId={selectedCalculation?.id}>
      <CreateFromCalculation offerId={offerId} processId={processId} />
    </SelectDocumentButton>
  );
}

export type SelectDocumentButtonProps = {
  selectedDocumentId?: string;
  defaultButtonText?: string;
  children?: ReactNode;
};

export function SelectDocumentButton({
  selectedDocumentId,
  defaultButtonText,
  children,
}: SelectDocumentButtonProps) {
  if (selectedDocumentId && selectedDocumentId !== "") {
    return (
      <Button size="sm" variant="outline" disabled onClick={() => {}}>
        <DocumentNumberById
          className="text-sm"
          documentId={selectedDocumentId}
        />
      </Button>
    );
  }

  return (
    <Dialog>
      <DialogTrigger className="border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 rounded-md border px-3">
        {(defaultButtonText && t(defaultButtonText)) ||
          t("-- nicht gewählt --")}
      </DialogTrigger>
      <DialogContent className="max-h-[80vh] min-w-[80vw] overflow-y-scroll">
        <DialogHeader>
          <DialogTitle>
            {t("Daten aus bestehender Kalkulation laden")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "Sie können hier eine Kalkulation auswählen, auf Basis derer das Angebot aktualisiert wird..",
            )}
          </DialogDescription>
        </DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

type CalculationDisplayRowProps = {
  calculation: Calculation;
};

function CalculationDisplayRow({ calculation }: CalculationDisplayRowProps) {
  return (
    <div className="grid grid-cols-2">
      <div className="flex items-center">
        <div className="border-r px-2">
          <DocumentNumberById className="text-sm" documentId={calculation.id} />
        </div>
        <div className="border-r px-2">
          <span>{calculation.subject}</span>
        </div>
      </div>
      <div className="flex items-center">
        <div className="px-2">
          <span>{formatDate(calculation.createdAt)}</span>
        </div>
        <div className="px-2">
          <span>{formatTime(calculation.createdAt)}</span>
        </div>
      </div>
    </div>
  );
}
